import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, ArrowLeft, Plus, Download, FileSpreadsheet, ShieldCheck, Settings as SettingsIcon, Users, IndianRupee, Wallet, TrendingUp, LifeBuoy } from "lucide-react";
import { api, inr, fmtDate, fmtDateTime, apiError } from "@/lib/api";
import { Skeleton, StatusBadge, EmptyState } from "@/components/common";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose,
} from "@/components/ui/dialog";
import { generateSchedulePdf } from "@/lib/statementPdf";
import { getIncludeDemo, useAnalytics, Toolbar, Kpi, Panel, TrendArea, Bars, Donut, RankTable, CHART_COLORS, inrShort, DemoToggle } from "@/lib/adminUI";
import { generateReportPdf } from "@/lib/reportPdf";

function Page({ title, subtitle, action, children }) {
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-2xl font-bold text-[#0B132B]">{title}</h1>{subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}</div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Table({ cols, children }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>{cols.map((c) => <th key={c} className="whitespace-nowrap px-4 py-3">{c}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
}

function useFetch(url) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(url).then((r) => setData(r.data)).catch(() => setData([])); }, [url]);
  return [data, setData];
}

const exportCsv = (rows, name) => {
  if (!rows?.length) return toast.error("Nothing to export");
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${name}.csv`; a.click();
  toast.success("Exported CSV");
};

const statusPill = (s) => {
  const map = { Active: "bg-emerald-100 text-emerald-700", Suspended: "bg-amber-100 text-amber-700", Blocked: "bg-red-100 text-red-700" };
  return <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${map[s] || map.Active}`}>{s || "Active"}</span>;
};

const schedPill = (s) => {
  const map = { Paid: "bg-emerald-100 text-emerald-700", Overdue: "bg-red-100 text-red-700", "Due Soon": "bg-amber-100 text-amber-700", Upcoming: "bg-slate-100 text-slate-600" };
  return <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${map[s] || map.Upcoming}`}>{s}</span>;
};

// ---------------- Customers ----------------
export function AdminCustomers() {
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [demo, setDemo] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams(); if (q.trim()) p.set("q", q.trim()); p.set("include_demo", String(demo));
      api.get(`/admin/customers?${p.toString()}`).then((r) => setRows(r.data)).catch(() => setRows([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, demo]);
  if (!rows) return <Skeleton className="h-72" />;
  return (
    <Page title="Customers" subtitle={`${rows.length} ${demo ? "customers (incl. testing)" : "real customers"}`} action={
      <div className="flex items-center gap-2">
        <DemoToggle demo={demo} onChange={setDemo} />
        <button onClick={() => exportCsv(rows.map((c) => ({ "ZRo Pe ID": c.customer_code, Name: c.name, Email: c.email, Mobile: c.mobile, Loans: c.loan_count, Active: c.active_loans, "Loan Amount": c.total_loan_amount, Outstanding: c.outstanding, "Total Paid": c.total_paid, Status: c.status, Joined: c.created_at, "Loan Numbers": (c.loan_numbers || []).join("; ") })), "ZRoPe-Customers")} data-testid="cust-export-csv" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-[#0B132B] hover:bg-slate-50"><Download size={15} /> Export CSV</button>
      </div>}>
      <div className="relative max-w-lg"><Search size={16} className="absolute left-3 top-2.5 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by ZRo Pe ID, name, mobile, email or loan number" className="w-full rounded-md border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#14B8A6]" data-testid="cust-search" /></div>
      {rows.length === 0 ? <EmptyState title="No customers found" desc="Try a different search." /> : (
        <Table cols={["ZRo Pe ID", "Customer", "Mobile", "Loans", "Loan Amount", "Outstanding", "Total Paid", "Status", "Joined", ""]}>
          {rows.map((c) => (
            <tr key={c.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-mono text-xs font-semibold text-[#14B8A6]">{c.customer_code || "—"}</td>
              <td className="px-4 py-3"><p className="font-medium text-[#0B132B]">{c.name}{c.is_demo && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">TEST</span>}</p><p className="text-xs text-slate-400">{c.email}</p></td>
              <td className="px-4 py-3 text-slate-600">{c.mobile ? `${c.mobile.slice(0, 2)}••••${c.mobile.slice(-2)}` : "—"}</td>
              <td className="px-4 py-3">{c.loan_count}<span className="text-xs text-slate-400"> ({c.active_loans} active)</span></td>
              <td className="px-4 py-3 text-slate-600">{inr(c.total_loan_amount)}</td>
              <td className="px-4 py-3 font-semibold text-[#0B132B]">{inr(c.outstanding)}</td>
              <td className="px-4 py-3 text-emerald-600">{inr(c.total_paid)}</td>
              <td className="px-4 py-3">{statusPill(c.status)}{c.overdue_count > 0 && <span className="ml-2 rounded-md bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700" data-testid={`overdue-flag-${c.id}`}>⚠ {c.overdue_count}</span>}</td>
              <td className="px-4 py-3 text-slate-500">{fmtDate(c.created_at)}</td>
              <td className="px-4 py-3"><Link to={`/admin/customers/${c.id}`} className="font-semibold text-[#14B8A6]" data-testid={`view-cust-${c.id}`}>View</Link></td>
            </tr>
          ))}
        </Table>
      )}
    </Page>
  );
}

export function AdminCustomerDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [pending, setPending] = useState(null);
  const [reason, setReason] = useState("");
  useEffect(() => { api.get(`/admin/customers/${id}`).then((r) => setC(r.data)).catch(() => toast.error("Not found")); }, [id]);
  if (!c) return <Skeleton className="h-72" />;
  const applyStatus = async (s, reasonText) => {
    try { await api.post(`/admin/customers/${id}/status`, { status: s, reason: reasonText }); setC({ ...c, status: s }); setPending(null); toast.success(`Customer set to ${s}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const onStatusPick = (s) => { if (s === "Active") applyStatus("Active", ""); else { setReason(""); setPending(s); } };
  return (
    <div className="space-y-5">
      <Link to="/admin/customers" className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#0B132B]"><ArrowLeft size={15} /> Back</Link>
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4"><span className="grid h-12 w-12 place-items-center rounded-full bg-[#0B132B] text-lg font-bold text-white">{(c.name || "U")[0]}</span><div><p className="font-display text-lg font-bold text-[#0B132B]">{c.name}</p><p className="text-sm text-slate-500">{c.email} • {c.mobile}</p></div></div>
          <div className="flex items-center gap-2">
            {statusPill(c.status)}
            <select value={c.status || "Active"} onChange={(e) => onStatusPick(e.target.value)} data-testid="cust-status-select" className="rounded-md border border-slate-200 px-3 py-1.5 text-sm font-semibold outline-none focus:border-[#14B8A6]">
              {["Active", "Suspended", "Blocked"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>
      <Dialog open={!!pending} onOpenChange={(o) => { if (!o) setPending(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{pending === "Blocked" ? "Block" : "Suspend"} customer</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Please provide a short reason. It is stored in the audit log.</p>
          <textarea data-testid="status-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} placeholder="e.g. Repeated payment defaults / under fraud review" className="mt-2 w-full rounded-md border border-slate-200 p-3 text-sm outline-none focus:border-[#14B8A6]" />
          <DialogFooter>
            <DialogClose asChild><button className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold">Cancel</button></DialogClose>
            <button data-testid="status-reason-confirm" onClick={() => { if (!reason.trim()) return toast.error("A reason is required"); applyStatus(pending, reason.trim()); }} className="rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white">Confirm {pending}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="grid gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"><h3 className="font-display font-bold text-[#0B132B]">Loans ({c.loans?.length || 0})</h3><div className="mt-3 space-y-2">{(c.loans || []).map((l) => (<div key={l.id} className="flex items-center justify-between rounded-md bg-slate-50 p-3 text-sm"><div><p className="font-medium text-[#0B132B]">{l.lender}</p><p className="text-xs text-slate-400">{l.loan_type}</p></div><div className="flex items-center gap-3"><div className="text-right"><p className="font-semibold text-[#0B132B]">{inr(l.outstanding)}</p><StatusBadge status={l.status} /></div><Link to={`/admin/loans/${l.id}/schedule`} data-testid={`cust-loan-schedule-${l.id}`} className="whitespace-nowrap font-semibold text-[#14B8A6]">EMI Schedule</Link></div></div>))}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"><h3 className="font-display font-bold text-[#0B132B]">Recent Payments</h3><div className="mt-3 space-y-2">{(c.payments || []).slice(0, 6).map((p) => (<div key={p.id} className="flex items-center justify-between text-sm"><span className="text-slate-500">{fmtDate(p.created_at)}</span><span className="font-semibold text-[#0B132B]">{inr(p.amount)}</span><StatusBadge status={p.status} /></div>))}</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"><h3 className="font-display font-bold text-[#0B132B]">Rewards</h3><p className="mt-2 font-display text-2xl font-bold text-[#0B132B]">{c.reward?.points?.toLocaleString("en-IN") || 0} pts</p></div>
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm"><h3 className="font-display font-bold text-[#0B132B]">Support Tickets</h3><div className="mt-3 space-y-2">{(c.tickets || []).map((t) => (<div key={t.id} className="flex items-center justify-between text-sm"><span className="text-[#0B132B]">{t.subject}</span><StatusBadge status={t.status} /></div>))}{(!c.tickets || !c.tickets.length) && <p className="text-sm text-slate-400">No tickets</p>}</div></div>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="font-display font-bold text-[#0B132B]">Status History</h3>
        <div className="mt-3 space-y-2" data-testid="status-history">
          {(c.status_history || []).length === 0 ? <p className="text-sm text-slate-400">No status changes yet.</p> : (c.status_history || []).map((h) => (
            <div key={h.id} className="flex items-start justify-between gap-3 rounded-md bg-slate-50 p-3 text-sm">
              <div>
                <p className="font-medium text-[#0B132B]">{h.before?.status || "—"} → {h.after?.status}</p>
                {h.after?.reason && <p className="text-xs text-slate-500">Reason: {h.after.reason}</p>}
                <p className="text-xs text-slate-400">by {h.admin_email}</p>
              </div>
              <span className="whitespace-nowrap text-xs text-slate-400">{fmtDateTime(h.timestamp)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------- Loans ----------------
export function AdminLoans() {
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [lender, setLender] = useState("ALL");
  const [f, setF] = useState({ customer_id: "", lender: "", loan_type: "Personal Loan", principal: "", emi_amount: "", interest_rate: "", tenure_months: "" });
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const load = () => {
    const p = new URLSearchParams();
    if (q.trim()) p.set("q", q.trim());
    if (status !== "ALL") p.set("status", status);
    if (lender !== "ALL") p.set("lender", lender);
    p.set("limit", size); p.set("skip", (page - 1) * size);
    api.get(`/admin/loans?${p.toString()}`).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, lenders: [] }));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q, status, lender, page, size]);
  useEffect(() => { api.get("/admin/customers").then((r) => setCustomers(r.data)).catch(() => {}); }, []);
  const rows = data?.items || [];
  const lenders = data?.lenders || [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / size)) : 1;
  const inputCls = "w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]";
  const selCls = "rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#14B8A6]";
  const add = async () => {
    try {
      const payload = { ...f, principal: +f.principal, emi_amount: +f.emi_amount, interest_rate: +f.interest_rate, tenure_months: +f.tenure_months };
      if (!payload.customer_id || !payload.lender || !payload.principal || !payload.emi_amount || !payload.tenure_months) return toast.error("Please fill all required fields with valid values");
      await api.post("/admin/loans", payload);
      setOpen(false); toast.success("Loan added & audit logged"); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Page title="Loans" subtitle={data ? `${data.total} loans` : ""} action={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><button data-testid="add-loan" className="inline-flex items-center gap-1.5 rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0D9488]"><Plus size={16} /> Manual Loan Entry</button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Manual Loan Entry</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="text-xs font-medium text-slate-600">Customer *</label><select className={inputCls} value={f.customer_id} onChange={(e) => setF({ ...f, customer_id: e.target.value })} data-testid="loan-customer"><option value="">Select</option>{customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}</select></div>
            <div><label className="text-xs font-medium text-slate-600">Lender *</label><input className={inputCls} value={f.lender} onChange={(e) => setF({ ...f, lender: e.target.value })} placeholder="HDFC Bank" /></div>
            <div><label className="text-xs font-medium text-slate-600">Type</label><input className={inputCls} value={f.loan_type} onChange={(e) => setF({ ...f, loan_type: e.target.value })} /></div>
            <div><label className="text-xs font-medium text-slate-600">Principal *</label><input type="number" className={inputCls} value={f.principal} onChange={(e) => setF({ ...f, principal: e.target.value })} /></div>
            <div><label className="text-xs font-medium text-slate-600">EMI *</label><input type="number" className={inputCls} value={f.emi_amount} onChange={(e) => setF({ ...f, emi_amount: e.target.value })} /></div>
            <div><label className="text-xs font-medium text-slate-600">Interest %</label><input type="number" className={inputCls} value={f.interest_rate} onChange={(e) => setF({ ...f, interest_rate: e.target.value })} /></div>
            <div><label className="text-xs font-medium text-slate-600">Tenure (months) *</label><input type="number" className={inputCls} value={f.tenure_months} onChange={(e) => setF({ ...f, tenure_months: e.target.value })} /></div>
          </div>
          <DialogFooter><DialogClose asChild><button className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold">Cancel</button></DialogClose><button onClick={add} data-testid="loan-save" className="rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white">Save loan</button></DialogFooter>
        </DialogContent>
      </Dialog>
    }>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1"><Search size={16} className="absolute left-3 top-2.5 text-slate-400" /><input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search customer, mobile, lender or loan ID" className="w-full rounded-md border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#14B8A6]" data-testid="loan-search" /></div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className={selCls} data-testid="loan-status-filter">{["ALL", "ON_TRACK", "DUE_SOON", "OVERDUE", "CLOSED"].map((s) => <option key={s} value={s}>{s === "ALL" ? "All statuses" : s.replace("_", " ")}</option>)}</select>
        <select value={lender} onChange={(e) => { setLender(e.target.value); setPage(1); }} className={selCls} data-testid="loan-lender-filter"><option value="ALL">All lenders</option>{lenders.map((l) => <option key={l} value={l}>{l}</option>)}</select>
        <select value={size} onChange={(e) => { setSize(+e.target.value); setPage(1); }} className={selCls} data-testid="loan-page-size">{[20, 50, 100].map((n) => <option key={n} value={n}>{n} / page</option>)}</select>
      </div>
      {!data ? <Skeleton className="h-72" /> : rows.length === 0 ? <EmptyState title="No loans found" desc="Try a different search or filter." /> : (
      <Table cols={["Loan Number", "ZRo Pe ID", "Customer", "Lender", "Type", "Principal", "Outstanding", "EMI", "Status", ""]}>
        {rows.map((l) => (
          <tr key={l.id} className="hover:bg-slate-50">
            <td className="px-4 py-3 font-mono text-xs font-semibold text-[#0B132B]">{l.loan_number || "—"}</td>
            <td className="px-4 py-3 font-mono text-xs text-[#14B8A6]">{l.customer_code || "—"}</td>
            <td className="px-4 py-3"><p className="font-medium text-[#0B132B]">{l.customer_name || "—"}</p>{l.customer_mobile && <p className="text-xs text-slate-400">{l.customer_mobile?.slice(0, 2)}••••{l.customer_mobile?.slice(-2)}</p>}</td>
            <td className="px-4 py-3 font-medium text-[#0B132B]">{l.lender}</td>
            <td className="px-4 py-3 text-slate-600">{l.loan_type}</td>
            <td className="px-4 py-3">{inr(l.principal)}</td>
            <td className="px-4 py-3 font-semibold text-[#0B132B]">{inr(l.outstanding)}</td>
            <td className="px-4 py-3">{inr(l.emi_amount)}</td>
            <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
            <td className="px-4 py-3"><Link to={`/admin/loans/${l.id}/schedule`} data-testid={`loan-schedule-${l.id}`} className="font-semibold text-[#14B8A6]">EMI Schedule</Link></td>
          </tr>
        ))}
      </Table>
      )}
      {data && data.total > size && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-slate-500">Page {page} of {totalPages} • {data.total} loans</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="loan-prev" className="rounded-md border border-slate-200 px-3 py-1.5 font-semibold disabled:opacity-50">Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="loan-next" className="rounded-md border border-slate-200 px-3 py-1.5 font-semibold disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </Page>
  );
}

// ---------------- Admin EMI Schedule ----------------
export function AdminLoanSchedule() {
  const { loanId } = useParams();
  const [d, setD] = useState(null);
  const [sfilter, setSfilter] = useState("ALL");
  useEffect(() => { api.get(`/admin/loans/${loanId}/schedule`).then((r) => setD(r.data)).catch(() => toast.error("Loan not found")); }, [loanId]);
  if (!d) return <Skeleton className="h-72" />;
  const { schedule, summary, loan } = d;
  const visible = schedule.filter((r) => sfilter === "ALL" ? true : sfilter === "Paid" ? r.status === "Paid" : sfilter === "Overdue" ? r.status === "Overdue" : r.status !== "Paid");
  const exportSchedCsv = () => exportCsv(visible.map((r) => ({ "EMI #": r.emi_no, "Due Date": fmtDate(r.due_date), Principal: r.principal, Interest: r.interest, EMI: r.emi, Outstanding: r.remaining_principal, Status: r.status, "Paid On": r.paid_date ? fmtDate(r.paid_date) : "" })), "ZRoPe-EMI-Schedule");
  const exportSchedPdf = () => { generateSchedulePdf({ ...d, schedule: visible }).catch(() => toast.error("Could not generate PDF")); };
  return (
    <div className="space-y-5">
      <Link to={`/admin/customers/${loan.customer_id}`} className="inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-[#0B132B]" data-testid="schedule-back"><ArrowLeft size={15} /> Back to customer</Link>
      <Page title="EMI Schedule" subtitle={`${loan.lender} • ${loan.loan_type} • ${loan.customer_name || ""}`} action={
        <div className="flex items-center gap-2">
          <select value={sfilter} onChange={(e) => setSfilter(e.target.value)} data-testid="sched-filter" className="rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-[#14B8A6]">
            {["ALL", "Paid", "Unpaid", "Overdue"].map((s) => <option key={s} value={s}>{s === "ALL" ? "All EMIs" : s}</option>)}
          </select>
          <button onClick={exportSchedCsv} data-testid="sched-export-csv" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#0B132B] hover:bg-slate-50"><Download size={15} /> CSV</button>
          <button onClick={exportSchedPdf} data-testid="sched-export-pdf" className="inline-flex items-center gap-1.5 rounded-md bg-[#14B8A6] px-3 py-2 text-sm font-semibold text-white hover:bg-[#0D9488]"><Download size={15} /> PDF</button>
        </div>
      }>
        <div className="grid gap-4 sm:grid-cols-4">
          {[["EMIs", summary.total], ["Paid", summary.paid], ["Upcoming", summary.upcoming], ["Overdue", summary.overdue]].map(([l, v]) => (
            <div key={l} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><p className="text-xs text-slate-500">{l}</p><p className="mt-1 font-display text-xl font-bold text-[#0B132B]">{v}</p></div>
          ))}
        </div>
        <Table cols={["#", "Due Date", "Principal", "Interest", "EMI", "Outstanding", "Status"]}>
          {visible.map((r) => (
            <tr key={r.emi_no} className={`hover:bg-slate-50 ${r.status === "Overdue" ? "bg-red-50/40" : ""}`} data-testid={`sched-row-${r.emi_no}`}>
              <td className="px-4 py-3 font-medium text-[#0B132B]">{r.emi_no}</td>
              <td className="px-4 py-3 text-slate-600">{fmtDate(r.due_date)}</td>
              <td className="px-4 py-3">{inr(r.principal)}</td>
              <td className="px-4 py-3">{inr(r.interest)}</td>
              <td className="px-4 py-3 font-semibold text-[#0B132B]">{inr(r.emi)}</td>
              <td className="px-4 py-3">{inr(r.remaining_principal)}</td>
              <td className="px-4 py-3">{schedPill(r.status)}{r.status === "Overdue" && <span className="ml-1 text-xs font-bold text-red-600">●</span>}</td>
            </tr>
          ))}
        </Table>
      </Page>
    </div>
  );
}

// ---------------- Payments ----------------
export function AdminPayments() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("ALL");
  const [method, setMethod] = useState("ALL");
  const [category, setCategory] = useState("ALL");
  const [lender, setLender] = useState("ALL");
  const [range, setRange] = useState({ from: "", to: "" });
  const [page, setPage] = useState(1);
  const [size] = useState(25);
  const [demo] = useState(getIncludeDemo());
  const [tick, setTick] = useState(0);
  const [busyTxn, setBusyTxn] = useState(null);
  const [overrideTxn, setOverrideTxn] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const selCls = "rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-[#14B8A6]";
  useEffect(() => {
    const t = setTimeout(() => {
      const p = new URLSearchParams();
      if (q.trim()) p.set("q", q.trim());
      if (status !== "ALL") p.set("status", status);
      if (method !== "ALL") p.set("method", method);
      if (category !== "ALL") p.set("category", category);
      if (lender !== "ALL") p.set("lender", lender);
      if (range.from) p.set("from_date", range.from);
      if (range.to) p.set("to_date", range.to);
      p.set("include_demo", String(demo)); p.set("limit", size); p.set("skip", (page - 1) * size);
      api.get(`/admin/payments?${p.toString()}`).then((r) => setData(r.data)).catch(() => setData({ items: [], total: 0, methods: [], lenders: [] }));
    }, 250);
    return () => clearTimeout(t);
  }, [q, status, method, lender, category, range, page, size, demo, tick]);
  const lenderAction = async (txn, action) => {
    setBusyTxn(txn + action);
    try {
      const { data: r } = await api.post(`/admin/payments/${txn}/lender-${action}`);
      if (r.idempotent) toast.info("Already processed — no change.");
      else if (action === "confirm") toast.success(`Lender confirmed. ₹${r.reward_credited || 0} reward credited to wallet.`);
      else toast.success(`Lender rejected. ₹${r.reward_voided || 0} pending reward voided.`);
      setTick((n) => n + 1);
    } catch (e) { toast.error(apiError(e)); } finally { setBusyTxn(null); }
  };
  const runSync = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post(`/admin/lender-sync/run`);
      toast.success(`Lender sync ran: ${r.confirmed} confirmed, ${r.rejected} rejected of ${r.processed} awaiting.`);
      setTick((n) => n + 1);
    } catch (e) { toast.error(apiError(e)); } finally { setSyncing(false); }
  };
  const rows = data?.items || [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / size)) : 1;
  const exportRows = () => exportCsv(rows.map((p) => ({ "Payment ID": p.payment_code, "ZRo Pe ID": p.customer_code, Customer: p.customer_name, "Loan Number": p.loan_number, Lender: p.lender, Type: p.loan_type, Amount: p.amount, Fee: p.fee, Method: p.method, Status: p.status, "Txn ID": p.txn_id, Gateway: p.gateway, Reason: p.reason || "", "Created At": p.created_at })), "ZRoPe-Payments");
  return (
    <Page title="Payments" subtitle={data ? `${data.total} transactions` : ""} action={<div className="flex gap-2"><button onClick={runSync} disabled={syncing} data-testid="run-lender-sync" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-[#0B132B] disabled:opacity-50">{syncing ? "Syncing…" : "Run Lender Sync"}</button><button onClick={exportRows} className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-[#0B132B]"><Download size={15} /> Export CSV</button></div>}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[240px] flex-1"><Search size={16} className="absolute left-3 top-2.5 text-slate-400" /><input value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} placeholder="Search Payment/Txn ID, ZPU ID, loan number, customer or lender" className="w-full rounded-md border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#14B8A6]" data-testid="pay-search" /></div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} className={selCls} data-testid="pay-status">{["ALL", "SUCCESS", "PENDING", "FAILED"].map((s) => <option key={s} value={s}>{s === "ALL" ? "All statuses" : s}</option>)}</select>
        <select value={method} onChange={(e) => { setMethod(e.target.value); setPage(1); }} className={selCls} data-testid="pay-method"><option value="ALL">All methods</option>{(data?.methods || []).map((m) => <option key={m} value={m}>{m.replace(/_/g, " ")}</option>)}</select>
        <select value={category} onChange={(e) => { setCategory(e.target.value); setPage(1); }} className={selCls} data-testid="pay-category"><option value="ALL">All categories</option><option value="LOAN_EMI">Loan EMI</option><option value="CREDIT_CARD">Credit Card</option></select>
        <select value={lender} onChange={(e) => { setLender(e.target.value); setPage(1); }} className={selCls} data-testid="pay-lender"><option value="ALL">All lenders</option>{(data?.lenders || []).map((l) => <option key={l} value={l}>{l}</option>)}</select>
        <input type="date" value={range.from} onChange={(e) => { setRange({ ...range, from: e.target.value }); setPage(1); }} className={selCls} data-testid="pay-from" />
        <input type="date" value={range.to} onChange={(e) => { setRange({ ...range, to: e.target.value }); setPage(1); }} className={selCls} data-testid="pay-to" />
      </div>
      <p className="text-xs text-slate-400">Lender confirmation is <b>automatic</b> (auto-sync simulates the lender webhook/poll in DEMO). Manual override is an admin fallback only. Failed/pending payments are excluded from collection totals; all changes are audited.</p>
      {!data ? <Skeleton className="h-72" /> : rows.length === 0 ? <EmptyState title="No payments found" desc="Try a different filter." /> : (
        <Table cols={["Payment ID", "ZRo Pe ID", "Customer", "Loan Number", "Lender", "Amount", "Method", "Status", "Lender Confirmation", "Date"]}>
          {rows.map((p) => {
            const st = p.payment_state;
            const pill = st === "LENDER_CONFIRMED" ? "bg-emerald-100 text-emerald-700"
              : st === "LENDER_REJECTED" ? "bg-red-100 text-red-600"
              : st === "AWAITING_LENDER_CONFIRMATION" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500";
            const label = st === "LENDER_CONFIRMED" ? "Confirmed" : st === "LENDER_REJECTED" ? "Rejected"
              : st === "AWAITING_LENDER_CONFIRMATION" ? "Awaiting" : st === "LEGACY_UNKNOWN" ? "Legacy" : (st || "—");
            const canAct = p.status === "SUCCESS" && (st === "AWAITING_LENDER_CONFIRMATION" || !st);
            return (
            <tr key={p.txn_id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-mono text-xs font-semibold text-[#0B132B]">{p.payment_code}</td>
              <td className="px-4 py-3 font-mono text-xs text-[#14B8A6]">{p.customer_code || "—"}</td>
              <td className="px-4 py-3 font-medium text-[#0B132B]">{p.customer_name || "—"}</td>
              <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.loan_number || "—"}</td>
              <td className="px-4 py-3 text-slate-600">{p.lender}</td>
              <td className="px-4 py-3 font-semibold text-[#0B132B]">{inr(p.amount)}{p.reward_amount ? <span className="block text-[10px] font-normal text-amber-600">reward ₹{p.reward_amount} {p.reward_status === "CREDITED" ? "credited" : p.reward_status === "VOID" ? "void" : "pending"}</span> : null}</td>
              <td className="px-4 py-3 text-slate-600">{p.method?.replace(/_/g, " ")}</td>
              <td className="px-4 py-3"><StatusBadge status={p.status} />{p.reason && p.status !== "SUCCESS" && <p className="mt-0.5 text-[11px] text-slate-400">{p.reason}</p>}</td>
              <td className="px-4 py-3">
                <span className={`inline-block rounded px-1.5 py-0.5 text-[10px] font-bold ${pill}`}>{label === "Awaiting" ? "Auto — Waiting for Lender" : label}</span>
                {canAct && (overrideTxn === p.txn_id ? (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <button data-testid={`lender-confirm-${p.txn_id}`} disabled={busyTxn === p.txn_id + "confirm"} onClick={() => lenderAction(p.txn_id, "confirm")} className="rounded bg-[#14B8A6] px-2 py-1 text-[11px] font-semibold text-white hover:bg-[#0D9488] disabled:opacity-50">Confirm</button>
                    <button data-testid={`lender-reject-${p.txn_id}`} disabled={busyTxn === p.txn_id + "reject"} onClick={() => lenderAction(p.txn_id, "reject")} className="rounded border border-slate-200 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50">Reject</button>
                    <button onClick={() => setOverrideTxn(null)} className="text-[11px] text-slate-400 hover:text-slate-600">Cancel</button>
                  </div>
                ) : (
                  <button data-testid={`lender-override-${p.txn_id}`} onClick={() => setOverrideTxn(p.txn_id)} className="mt-1.5 block text-[11px] font-semibold text-slate-400 hover:text-[#0B132B]">More → Manual override</button>
                ))}
              </td>
              <td className="px-4 py-3 text-slate-500">{fmtDate(p.created_at)}</td>
            </tr>
          );})}
        </Table>
      )}
      {data && data.total > size && (
        <div className="mt-3 flex items-center justify-between text-sm">
          <span className="text-slate-500">Page {page} of {totalPages} • {data.total} payments</span>
          <div className="flex gap-2">
            <button disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="pay-prev" className="rounded-md border border-slate-200 px-3 py-1.5 font-semibold disabled:opacity-50">Prev</button>
            <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="pay-next" className="rounded-md border border-slate-200 px-3 py-1.5 font-semibold disabled:opacity-50">Next</button>
          </div>
        </div>
      )}
    </Page>
  );
}

// ---------------- Rewards ----------------
export function AdminRewards() {
  const [d] = useFetch("/admin/rewards");
  if (!d) return <Skeleton className="h-72" />;
  return (
    <Page title="Rewards" subtitle={`${d.total_transactions} reward ledger entries`}>
      <Table cols={["Coupon", "Cost (pts)", "Code", "Expiry", "Status"]}>
        {d.coupons.map((c) => (
          <tr key={c.id} className="hover:bg-slate-50">
            <td className="px-4 py-3 font-medium text-[#0B132B]">{c.title}</td>
            <td className="px-4 py-3">{c.cost_points}</td>
            <td className="px-4 py-3 font-mono text-xs">{c.code}</td>
            <td className="px-4 py-3 text-slate-600">{c.expiry}</td>
            <td className="px-4 py-3"><StatusBadge status={c.active ? "SUCCESS" : "CLOSED"} /></td>
          </tr>
        ))}
      </Table>
    </Page>
  );
}

// ---------------- Offers ----------------
export function AdminOffers() {
  const [rows, setRows] = useFetch("/admin/offers");
  const [f, setF] = useState({ title: "", partner: "", description: "", category: "Partner", eligibility: "All users", expiry: "31 Dec 2026" });
  const [open, setOpen] = useState(false);
  const inputCls = "w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]";
  if (!rows) return <Skeleton className="h-72" />;
  const create = async () => {
    if (!f.title) return toast.error("Title is required");
    try { const { data } = await api.post("/admin/offers", f); setRows([data, ...rows]); setOpen(false); toast.success("Offer created & logged"); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Page title="Offers" subtitle={`${rows.length} offers`} action={
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><button data-testid="add-offer" className="inline-flex items-center gap-1.5 rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0D9488]"><Plus size={16} /> Create Offer</button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Create Partner Offer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <input className={inputCls} placeholder="Offer title *" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="offer-title" />
            <input className={inputCls} placeholder="Partner" value={f.partner} onChange={(e) => setF({ ...f, partner: e.target.value })} />
            <textarea rows={2} className={inputCls} placeholder="Description" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
            <select className={inputCls} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>{["Partner", "Cashback", "Discount", "EMI Offer"].map((c) => <option key={c}>{c}</option>)}</select>
          </div>
          <DialogFooter><DialogClose asChild><button className="rounded-md border border-slate-200 px-4 py-2 text-sm font-semibold">Cancel</button></DialogClose><button onClick={create} data-testid="offer-save" className="rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white">Create</button></DialogFooter>
        </DialogContent>
      </Dialog>
    }>
      <Table cols={["Title", "Partner", "Category", "Eligibility", "Expiry", "Status"]}>
        {rows.map((o) => (
          <tr key={o.id} className="hover:bg-slate-50">
            <td className="px-4 py-3 font-medium text-[#0B132B]">{o.title}</td>
            <td className="px-4 py-3 text-slate-600">{o.partner}</td>
            <td className="px-4 py-3 text-slate-600">{o.category}</td>
            <td className="px-4 py-3 text-slate-600">{o.eligibility}</td>
            <td className="px-4 py-3 text-slate-600">{o.expiry}</td>
            <td className="px-4 py-3"><StatusBadge status={o.active ? "SUCCESS" : "CLOSED"} /></td>
          </tr>
        ))}
      </Table>
    </Page>
  );
}

// ---------------- Notifications (admin view) ----------------
export function AdminNotifications() {
  return <Page title="Notifications" subtitle="Broadcast & delivery management"><EmptyState title="Delivery channels in DEMO mode" desc="SMS, WhatsApp, Email and Push providers are integration-ready. Connect a provider to broadcast notifications to customers." /></Page>;
}

// ---------------- Support ----------------
export function AdminSupport() {
  const [rows, setRows] = useFetch("/admin/support");
  if (!rows) return <Skeleton className="h-72" />;
  const setStatus = async (t, status) => {
    try { await api.post(`/admin/support/${t.id}/status`, { status }); setRows(rows.map((r) => (r.id === t.id ? { ...r, status } : r))); toast.success("Status updated & logged"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const recheck = async (t) => {
    try {
      const { data } = await api.post(`/admin/support/${t.id}/recheck`);
      if (data.resolved) { setRows(rows.map((r) => (r.id === t.id ? { ...r, status: "Resolved" } : r))); toast.success(data.message); }
      else toast.warning(data.message);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Page title="Support Tickets" subtitle={`${rows.length} tickets`}>
      {rows.length === 0 ? <EmptyState title="No tickets" desc="Customer tickets will appear here." /> : (
        <Table cols={["Ticket", "Customer", "Subject", "Category", "Created", "Status", ""]}>
          {rows.map((t) => (
            <tr key={t.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-mono text-xs">{t.ticket_no}</td>
              <td className="px-4 py-3 font-medium text-[#0B132B]">{t.customer_name}</td>
              <td className="px-4 py-3 text-slate-600">{t.subject}</td>
              <td className="px-4 py-3 text-slate-600">{t.category}</td>
              <td className="px-4 py-3 text-slate-500">{fmtDate(t.created_at)}</td>
              <td className="px-4 py-3">
                <select value={t.status} onChange={(e) => setStatus(t, e.target.value)} className="rounded-md border border-slate-200 px-2 py-1 text-xs" data-testid={`ticket-status-${t.id}`}>
                  {["Open", "In Progress", "Resolved", "Closed"].map((s) => <option key={s}>{s}</option>)}
                </select>
              </td>
              <td className="px-4 py-3">
                <button onClick={() => recheck(t)} data-testid={`ticket-recheck-${t.id}`} className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-semibold text-[#14B8A6] hover:bg-slate-50">Re-check payment</button>
              </td>
            </tr>
          ))}
        </Table>
      )}
    </Page>
  );
}

// ---------------- Reports ----------------
const REPORT_TABS = ["Executive", "Collections", "Payments", "Loans", "Lenders", "Aging", "DPD & Buckets", "Payment Types", "Wallet & Rewards", "Margin", "Sync"];
export function AdminReports() {
  const a = useAnalytics();
  const [tab, setTab] = useState("Executive");
  if (!a.data) return <Skeleton className="h-72" />;
  const d = a.data, o = d.overview;
  return (
    <Page title="Reporting Center" subtitle="Real-data business reports with drill-down & CSV export" action={
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => generateReportPdf(a.data, { title: `${tab} Report`, slug: tab.toLowerCase() })} data-testid="report-export-pdf" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#0B132B] hover:bg-slate-50"><Download size={15} /> PDF</button>
        <button onClick={() => exportCsv(a.data.lenders.map((l) => ({ Lender: l.lender, Loans: l.loans, Active: l.active, Disbursed: l.disbursed, Collected: l.collected, Outstanding: l.outstanding, "Success %": l.success_rate })), "ZRoPe-Lender-Report")} data-testid="report-export-csv" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#0B132B] hover:bg-slate-50"><Download size={15} /> CSV</button>
        <Toolbar a={a} />
      </div>}>
      <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-white p-1 w-fit">
        {REPORT_TABS.map((t) => <button key={t} data-testid={`report-tab-${t}`} onClick={() => setTab(t)} className={`rounded-md px-3.5 py-1.5 text-sm font-semibold transition-colors ${tab === t ? "bg-[#0B132B] text-white" : "text-slate-500 hover:text-[#0B132B]"}`}>{t}</button>)}
      </div>

      {tab === "Executive" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Collected" value={inrShort(o.total_collected)} delta={o.window_collected_delta} icon={IndianRupee} />
            <Kpi label="Outstanding" value={inrShort(o.outstanding)} accent="#F59E0B" icon={Wallet} />
            <Kpi label="Collection Rate" value={`${o.collection_rate}%`} accent="#10B981" icon={TrendingUp} />
            <Kpi label="Success Rate" value={`${o.success_rate}%`} delta={o.window_success_delta} accent="#10B981" icon={ShieldCheck} />
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <Panel title="Collection Trend" className="lg:col-span-2"><TrendArea data={d.collections.series} /></Panel>
            <Panel title="Payment Status"><Donut data={d.payments.status_counts} /></Panel>
          </div>
        </div>
      )}
      {tab === "Collections" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Total Collected" value={inrShort(o.total_collected)} icon={IndianRupee} />
            <Kpi label="Outstanding" value={inrShort(d.collections.outstanding)} accent="#F59E0B" icon={Wallet} />
            <Kpi label="Overdue Amount" value={inrShort(d.collections.overdue_amount)} accent="#EF4444" icon={Download} />
            <Kpi label="Collection Rate" value={`${o.collection_rate}%`} accent="#10B981" icon={TrendingUp} />
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <Panel title="Collection Trend" className="lg:col-span-2"><TrendArea data={d.collections.series} /></Panel>
            <Panel title="Collection by Lender"><Donut data={d.collections.by_lender} money /></Panel>
          </div>
          <Panel title="Collection by Loan Type"><Bars data={d.collections.by_type.map((x) => ({ date: x.name, amount: x.value }))} keys={["amount"]} money /></Panel>
        </div>
      )}
      {tab === "Payments" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Total Payments" value={o.payment_success + o.payment_failed + o.payment_pending} icon={IndianRupee} />
            <Kpi label="Successful" value={o.payment_success} accent="#10B981" icon={ShieldCheck} />
            <Kpi label="Failed" value={o.payment_failed} accent="#EF4444" icon={Download} />
            <Kpi label="Success Rate" value={`${o.success_rate}%`} accent="#10B981" icon={TrendingUp} />
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <Panel title="Volume (Success vs Failed)" className="lg:col-span-2"><Bars data={d.payments.series} keys={["success", "failed"]} colors={[CHART_COLORS[0], "#EF4444"]} /></Panel>
            <Panel title="Payment Methods"><Donut data={d.payments.method_dist} /></Panel>
          </div>
          {d.payments.failure_reasons.length > 0 && <Panel title="Failure Reasons"><Bars data={d.payments.failure_reasons.map((x) => ({ date: x.name, count: x.value }))} keys={["count"]} /></Panel>}
        </div>
      )}
      {tab === "Loans" && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Total Loans" value={o.total_loans} icon={Wallet} />
            <Kpi label="Active" value={o.active_loans} accent="#10B981" icon={TrendingUp} />
            <Kpi label="Closed" value={o.closed_loans} accent="#6366F1" icon={ShieldCheck} />
            <Kpi label="Avg Loan" value={inrShort(d.loans.avg_amount)} icon={IndianRupee} />
          </div>
          <div className="grid gap-4 lg:grid-cols-3">
            <Panel title="New Loans / Month" className="lg:col-span-2"><Bars data={d.loans.new_by_month.map((x) => ({ date: x.month, loans: x.loans }))} keys={["loans"]} /></Panel>
            <Panel title="Loan Status"><Donut data={d.loans.status_dist} /></Panel>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <Panel title="Loan Type"><Donut data={d.loans.type_dist} /></Panel>
            <Panel title="Lender Distribution"><Donut data={d.loans.lender_dist} /></Panel>
          </div>
        </div>
      )}
      {tab === "Lenders" && (
        <Panel title="Lender Performance" subtitle="Collections, outstanding & success by lender">
          <RankTable cols={["Lender", "Loans", "Active", "Disbursed", "Collected", "Outstanding", "Success"]} rows={d.lenders}
            render={(l) => (
              <tr key={l.lender} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-[#0B132B]">{l.lender}</td>
                <td className="px-4 py-3">{l.loans}</td><td className="px-4 py-3">{l.active}</td>
                <td className="px-4 py-3 text-slate-600">{inr(l.disbursed)}</td>
                <td className="px-4 py-3 font-semibold text-emerald-600">{inr(l.collected)}</td>
                <td className="px-4 py-3 text-slate-600">{inr(l.outstanding)}</td>
                <td className="px-4 py-3"><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold">{l.success_rate}%</span></td>
              </tr>
            )} />
        </Panel>
      )}
      {tab === "Aging" && (
        <div className="space-y-4">
          <Panel title="Outstanding Aging" subtitle="Portfolio by days overdue"><Bars data={d.aging.map((x) => ({ date: x.bucket, amount: x.amount }))} keys={["amount"]} money /></Panel>
          <RankTable cols={["Bucket", "Outstanding", "Loans", "% of Portfolio"]} rows={d.aging}
            render={(b) => {
              const total = d.aging.reduce((s, x) => s + x.amount, 0) || 1;
              return (<tr key={b.bucket} className="hover:bg-slate-50"><td className="px-4 py-3 font-medium text-[#0B132B]">{b.bucket}</td><td className="px-4 py-3 font-semibold">{inr(b.amount)}</td><td className="px-4 py-3">{b.loans}</td><td className="px-4 py-3 text-slate-500">{Math.round(100 * b.amount / total)}%</td></tr>);
            }} />
        </div>
      )}
      {tab === "DPD & Buckets" && (
        <div className="space-y-4">
          <div className="rounded-lg bg-teal-50 p-3 text-xs text-[#0B132B]"><b>Source: Lender / API.</b> DPD & buckets are lender-controlled; ZRo Pe never calculates them.</div>
          {!d.dpd_buckets.has_lender_data ? <div className="rounded-lg bg-slate-50 p-4 text-sm italic text-slate-400">Not available from lender</div> : (
            <Panel title="Bucket Report" right={<span className="text-xs italic text-slate-400">Historical movement unavailable</span>}>
              <RankTable cols={["Bucket", "Accounts", "POS", "Overdue", "Collection", "Collection %"]} rows={d.dpd_buckets.buckets}
                render={(b) => (<tr key={b.bucket} className="hover:bg-slate-50"><td className="px-4 py-3 font-medium text-[#0B132B]">{b.bucket}</td><td className="px-4 py-3">{b.accounts}</td><td className="px-4 py-3 text-slate-600">{b.bucket === "Not available from lender" ? "—" : inr(b.pos)}</td><td className="px-4 py-3 text-slate-600">{b.bucket === "Not available from lender" ? "—" : inr(b.overdue)}</td><td className="px-4 py-3 font-semibold text-emerald-600">{inr(b.collection)}</td><td className="px-4 py-3">{b.bucket === "Not available from lender" ? "—" : `${b.collection_rate}%`}</td></tr>)} />
            </Panel>
          )}
        </div>
      )}
      {tab === "Payment Types" && (
        <Panel title="Payment Type Report">
          <RankTable cols={["Type", "Txns", "Success ₹", "Failed ₹", "Coll. %", "Wallet", "Cash", "Discount", "Reward Cost"]} rows={d.payment_types}
            render={(p) => (<tr key={p.type} className="hover:bg-slate-50"><td className="px-4 py-3 font-medium text-[#0B132B]">{p.type}</td><td className="px-4 py-3">{p.count}</td><td className="px-4 py-3 font-semibold text-emerald-600">{inr(p.success_amount)}</td><td className="px-4 py-3 text-red-500">{inr(p.failed_amount)}</td><td className="px-4 py-3">{p.collection_rate}%</td><td className="px-4 py-3 text-purple-600">{inr(p.wallet_used)}</td><td className="px-4 py-3 text-slate-600">{inr(p.customer_cash)}</td><td className="px-4 py-3 text-slate-600">{inr(p.discount)}</td><td className="px-4 py-3 text-slate-600">{inr(p.reward_cost)}</td></tr>)} />
        </Panel>
      )}
      {tab === "Wallet & Rewards" && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Kpi label="Available" value={inrShort(d.wallet.available_balance)} icon={Wallet} accent="#10B981" />
          <Kpi label="Pending Rewards" value={inrShort(d.wallet.pending_balance)} icon={ShieldCheck} accent="#F59E0B" />
          <Kpi label="Rewards Credited" value={inrShort(d.wallet.rewards_credited)} icon={TrendingUp} />
          <Kpi label="Wallet Liability" value={inrShort(d.wallet.liability)} icon={IndianRupee} accent="#EF4444" />
          <Kpi label="Total Used" value={inrShort(d.wallet.total_used)} icon={Wallet} accent="#8B5CF6" />
          <Kpi label="Reward Cost" value={inrShort(d.wallet.reward_cost)} icon={Download} accent="#0B132B" />
          <Kpi label="Customers w/ Balance" value={d.wallet.customers_with_balance} icon={Users} accent="#0EA5E9" />
          <Kpi label="Using Wallet" value={d.wallet.customers_using_wallet} icon={Users} accent="#6366F1" />
        </div>
      )}
      {tab === "Margin" && (
        <div className="space-y-4">
          <div className="rounded-lg bg-[#0B132B] p-3 text-xs font-semibold text-[#14B8A6]">ADMIN ONLY — never shown to customers.</div>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            <Kpi label="Gross" value={inrShort(d.margin.gross)} icon={IndianRupee} />
            <Kpi label="Discount" value={inrShort(d.margin.discount)} accent="#F59E0B" icon={Download} hint={`${d.margin.discount_pct}%`} />
            <Kpi label="Reward Cost" value={inrShort(d.margin.reward_cost)} accent="#8B5CF6" icon={Wallet} hint={`${d.margin.reward_pct}%`} />
            <Kpi label="Net Margin" value={inrShort(d.margin.net_margin)} accent="#10B981" icon={TrendingUp} hint={`${d.margin.margin_pct}%`} />
          </div>
          <Panel title="Margin by Lender">
            <RankTable cols={["Lender", "Gross", "Discount", "Reward Cost", "Net Margin", "Margin %"]} rows={d.margin.by_lender}
              render={(x) => (<tr key={x.name} className="hover:bg-slate-50"><td className="px-4 py-3 font-medium text-[#0B132B]">{x.name}</td><td className="px-4 py-3 text-slate-600">{inr(x.gross)}</td><td className="px-4 py-3 text-amber-600">{inr(x.discount)}</td><td className="px-4 py-3 text-purple-600">{inr(x.reward_cost)}</td><td className="px-4 py-3 font-semibold text-emerald-600">{inr(x.net_margin)}</td><td className="px-4 py-3">{x.margin_pct}%</td></tr>)} />
          </Panel>
        </div>
      )}
      {tab === "Sync" && (
        <div className="space-y-4">
          <div className="rounded-lg bg-amber-50 p-3 text-xs font-semibold text-amber-700">DEMO / SAMPLE DATA — no real lender API connected yet.</div>
          <Panel title="Lender Sync Health">
            <RankTable cols={["Lender", "Status", "Source", "Synced", "Failed", "Last Sync"]} rows={d.sync_health}
              render={(s) => { const pill = s.status === "Healthy" ? "bg-emerald-100 text-emerald-700" : s.status === "Warning" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"; return (<tr key={s.lender} className="hover:bg-slate-50"><td className="px-4 py-3 font-medium text-[#0B132B]">{s.lender}</td><td className="px-4 py-3"><span className={`rounded px-2 py-0.5 text-xs font-bold ${pill}`}>{s.status}</span></td><td className="px-4 py-3">{s.is_demo ? <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-700">DEMO</span> : s.source}</td><td className="px-4 py-3 text-emerald-600">{s.records_synced}</td><td className="px-4 py-3 text-red-500">{s.records_failed}</td><td className="px-4 py-3 text-slate-500">{s.last_synced_at ? fmtDateTime(s.last_synced_at) : "Not available from lender"}</td></tr>); }} />
          </Panel>
        </div>
      )}
    </Page>
  );
}

// ---------------- Analytics ----------------
export function AdminAnalytics() {
  const a = useAnalytics();
  if (!a.data) return <Skeleton className="h-72" />;
  const d = a.data, c = d.customers;
  return (
    <Page title="Analytics Center" subtitle="Customer, loan & lender intelligence on real data" action={
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => generateReportPdf(a.data, { title: "Analytics Report", slug: "analytics" })} data-testid="analytics-export-pdf" className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#0B132B] hover:bg-slate-50"><Download size={15} /> PDF</button>
        <Toolbar a={a} />
      </div>}>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Total Customers" value={c.total} icon={Users} accent="#0EA5E9" />
        <Kpi label="Active Customers" value={c.active} accent="#10B981" icon={TrendingUp} />
        <Kpi label="Multi-loan Customers" value={c.multi_loan} accent="#6366F1" icon={Wallet} />
        <Kpi label="Avg Loans / Customer" value={c.avg_loans} icon={IndianRupee} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Customer Growth" subtitle="New customers per month"><Bars data={c.growth.map((x) => ({ date: x.month, customers: x.customers }))} keys={["customers"]} /></Panel>
        <Panel title="New Loans / Month"><Bars data={d.loans.new_by_month.map((x) => ({ date: x.month, loans: x.loans }))} keys={["loans"]} colors={["#6366F1"]} /></Panel>
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <Panel title="Loan Status"><Donut data={d.loans.status_dist} /></Panel>
        <Panel title="Loan Type"><Donut data={d.loans.type_dist} /></Panel>
        <Panel title="Lender Share"><Donut data={d.loans.lender_dist} /></Panel>
      </div>
      <Panel title="Lender Comparison">
        <RankTable cols={["Lender", "Loans", "Disbursed", "Collected", "Outstanding", "Success"]} rows={d.lenders}
          render={(l) => (
            <tr key={l.lender} className="hover:bg-slate-50">
              <td className="px-4 py-3 font-medium text-[#0B132B]">{l.lender}</td><td className="px-4 py-3">{l.loans}</td>
              <td className="px-4 py-3 text-slate-600">{inr(l.disbursed)}</td>
              <td className="px-4 py-3 font-semibold text-emerald-600">{inr(l.collected)}</td>
              <td className="px-4 py-3 text-slate-600">{inr(l.outstanding)}</td>
              <td className="px-4 py-3"><span className="rounded-md bg-slate-100 px-2 py-0.5 text-xs font-semibold">{l.success_rate}%</span></td>
            </tr>
          )} />
      </Panel>
    </Page>
  );
}

// ---------------- Audit Logs ----------------
export function AdminAudit() {
  const [rows] = useFetch("/admin/audit-logs");
  if (!rows) return <Skeleton className="h-72" />;
  return (
    <Page title="Audit Logs" subtitle="Immutable record of sensitive admin activity">
      {rows.length === 0 ? <EmptyState icon={ShieldCheck} title="No audit entries yet" desc="Sensitive admin actions (loan entry, offer creation, ticket updates) will be recorded here." /> : (
        <Table cols={["Time", "Admin", "Action", "Entity", "Entity ID", "IP"]}>
          {rows.map((a) => (
            <tr key={a.id} className="hover:bg-slate-50">
              <td className="px-4 py-3 text-slate-500">{fmtDateTime(a.timestamp)}</td>
              <td className="px-4 py-3 font-medium text-[#0B132B]">{a.admin_email}</td>
              <td className="px-4 py-3"><span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold">{a.action}</span></td>
              <td className="px-4 py-3 text-slate-600">{a.entity}</td>
              <td className="px-4 py-3 font-mono text-xs text-slate-400">{a.entity_id?.slice(0, 8)}</td>
              <td className="px-4 py-3 text-slate-500">{a.ip || "—"}</td>
            </tr>
          ))}
        </Table>
      )}
    </Page>
  );
}

// ---------------- Users & Roles ----------------
export function AdminUsers() {
  const [rows] = useFetch("/admin/users");
  if (!rows) return <Skeleton className="h-72" />;
  return (
    <Page title="Users & Roles" subtitle="Staff accounts and access levels">
      <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">Roles: CUSTOMER, SUPPORT, OPERATIONS, ADMIN, SUPER_ADMIN — extensible & enforced server-side.</div>
      <Table cols={["Name", "Email", "Role"]}>
        {rows.map((u) => (
          <tr key={u.id} className="hover:bg-slate-50">
            <td className="px-4 py-3 font-medium text-[#0B132B]">{u.name}</td>
            <td className="px-4 py-3 text-slate-600">{u.email}</td>
            <td className="px-4 py-3"><span className="rounded-md bg-[#0B132B] px-2 py-0.5 text-xs font-semibold text-[#14B8A6]">{u.role}</span></td>
          </tr>
        ))}
      </Table>
    </Page>
  );
}

// ---------------- Settings ----------------
function FeatureControls() {
  const [feats, setFeats] = useState(null);
  const [limits, setLimits] = useState({});
  const [savingLimit, setSavingLimit] = useState(null);
  const [saving, setSaving] = useState(null);
  const [reasons, setReasons] = useState({});
  const [resetEmail, setResetEmail] = useState("");
  const [resetGame, setResetGame] = useState("");
  const [resetting, setResetting] = useState(false);
  const GAME_LIMIT_KEYS = { game_daily_spin: "Daily Spin", game_scratch: "Scratch Card", game_ball: "Ball Challenge" };
  const load = () => api.get("/admin/feature-flags").then((r) => { setFeats(r.data.features); setLimits(r.data.game_limits || {}); }).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);
  const setStatus = async (key, status) => {
    setSaving(key);
    try { const { data } = await api.put("/admin/feature-flags", { key, status, reason: reasons[key] || "" }); setFeats(data.features); toast.success(`${data.features[key].label} → ${status}`); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(null); }
  };
  const saveLimit = async (key) => {
    const val = parseInt(limits[key], 10);
    if (!Number.isInteger(val) || val < 1 || val > 100) { toast.error("Enter a whole number between 1 and 100"); return; }
    setSavingLimit(key);
    try { const { data } = await api.put("/admin/game-limits", { key, limit: val }); setLimits(data.game_limits); toast.success(`${GAME_LIMIT_KEYS[key]} → ${val} chance(s)/day`); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingLimit(null); }
  };
  const doReset = async () => {
    if (!resetEmail.trim()) { toast.error("Enter a customer email"); return; }
    setResetting(true);
    try { const { data } = await api.post("/admin/games/reset-attempts", { email: resetEmail.trim(), game_key: resetGame || undefined }); toast.success(data.message); }
    catch (e) { toast.error(apiError(e)); } finally { setResetting(false); }
  };
  const badge = (s) => ({ LIVE: "bg-emerald-50 text-emerald-700", MAINTENANCE: "bg-amber-50 text-amber-700", DISABLED: "bg-red-50 text-red-600" }[s] || "bg-slate-100 text-slate-500");
  const groups = ["Games", "Customer Modules", "Transactional"];
  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="admin-feature-controls">
      <h3 className="flex items-center gap-2 font-display font-bold text-[#0B132B]"><ShieldCheck size={16} /> Feature Controls</h3>
      <p className="mt-1 text-xs text-slate-400">Emergency enable/disable for customer-facing modules. Server-authoritative, audited, effective immediately. Core security, account, loan & payment history are always available and not controllable here.</p>
      {!feats ? <Skeleton className="mt-3 h-40" /> : groups.map((g) => (
        <div key={g} className="mt-4">
          <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{g}</p>
          <div className="mt-2 space-y-2">
            {Object.entries(feats).filter(([, v]) => v.group === g).map(([key, v]) => (
              <div key={key} className="rounded-md bg-slate-50 px-3 py-2.5" data-testid={`feature-row-${key}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="text-sm font-semibold text-[#0B132B]">{v.label}</span>
                    <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${badge(v.status)}`} data-testid={`feature-status-${key}`}>{v.status}</span>
                  </div>
                  <div className="flex gap-1">
                    {["LIVE", "MAINTENANCE", "DISABLED"].map((s) => (
                      <button key={s} onClick={() => setStatus(key, s)} disabled={saving === key || v.status === s} data-testid={`feature-set-${key}-${s}`}
                        className={`rounded px-2 py-1 text-[11px] font-semibold transition-colors ${v.status === s ? "bg-[#0B132B] text-white" : "border border-slate-200 text-slate-600 hover:border-[#14B8A6]"} disabled:opacity-50`}>{s}</button>
                    ))}
                  </div>
                </div>
                {GAME_LIMIT_KEYS[key] && (
                  <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-[#14B8A6]/20 bg-[#14B8A6]/5 px-2.5 py-2">
                    <label className="text-xs font-semibold text-[#0B132B]" htmlFor={`limit-${key}`}>Chances per user per day</label>
                    <input id={`limit-${key}`} type="number" min="1" max="100" data-testid={`game-limit-input-${key}`}
                      value={limits[key] ?? 1} onChange={(e) => setLimits({ ...limits, [key]: e.target.value })}
                      className="w-20 rounded border border-slate-300 px-2 py-1 text-sm outline-none focus:border-[#14B8A6]" />
                    <button onClick={() => saveLimit(key)} disabled={savingLimit === key} data-testid={`game-limit-save-${key}`}
                      className="rounded bg-[#14B8A6] px-3 py-1 text-xs font-bold text-white hover:bg-[#0D9488] disabled:opacity-60">{savingLimit === key ? "Saving…" : "Save"}</button>
                    <span className="text-[11px] text-slate-400">Applies per customer, per day. Reward/history is never deleted. A disabled/maintenance game still blocks play regardless of this number.</span>
                  </div>
                )}
                {v.changed_by && <p className="mt-1 text-[11px] text-slate-400">Last changed by {v.changed_by} • {new Date(v.changed_at).toLocaleString()}{v.reason ? ` • ${v.reason}` : ""}</p>}
                <input value={reasons[key] || ""} onChange={(e) => setReasons({ ...reasons, [key]: e.target.value })} placeholder="Reason for change (optional, logged)" data-testid={`feature-reason-${key}`}
                  className="mt-1.5 w-full rounded border border-slate-200 px-2 py-1 text-xs outline-none focus:border-[#14B8A6]" />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 px-3 py-3" data-testid="reset-attempts-panel">
        <p className="text-sm font-bold text-[#0B132B]">Reset Today's Attempts (testing)</p>
        <p className="mt-0.5 text-[11px] text-amber-700">Clears today's attempt counter + play records for one customer so they can replay immediately. Coin balances and reward history are kept.</p>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} placeholder="customer@email.com" data-testid="reset-attempts-email"
            className="min-w-[220px] flex-1 rounded border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-[#14B8A6]" />
          <select value={resetGame} onChange={(e) => setResetGame(e.target.value)} data-testid="reset-attempts-game"
            className="rounded border border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-[#14B8A6]">
            <option value="">All games</option>
            <option value="game_daily_spin">Daily Spin</option>
            <option value="game_scratch">Scratch Card</option>
            <option value="game_ball">Ball Challenge</option>
          </select>
          <button onClick={doReset} disabled={resetting} data-testid="reset-attempts-btn"
            className="rounded bg-[#0B132B] px-3 py-1.5 text-xs font-bold text-white hover:bg-black disabled:opacity-60">{resetting ? "Resetting…" : "Reset Attempts"}</button>
        </div>
      </div>
    </div>
  );
}

function SupportContactSettings() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/support-contact").then((r) => setC(r.data)).catch(() => setC({ whatsapp: "", phone: "", email: "", display_name: "" })); }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/support-contact", c); setC({ whatsapp: data.whatsapp, phone: data.phone, email: data.email, display_name: data.display_name }); toast.success("Support contact saved."); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const field = (key, label, ph) => (
    <div><label className="text-xs font-semibold text-[#0B132B]">{label}</label>
      <input data-testid={`support-contact-${key}`} value={c[key] || ""} onChange={(e) => setC({ ...c, [key]: e.target.value })} placeholder={ph}
        className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm outline-none focus:border-[#14B8A6]" /></div>
  );
  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="support-contact-settings">
      <h3 className="flex items-center gap-2 font-display font-bold text-[#0B132B]"><LifeBuoy size={16} /> Support Contact</h3>
      <p className="mt-1 text-xs text-slate-400">Shown to customers across the app. Leave a field blank to hide that channel.</p>
      {!c ? <Skeleton className="mt-3 h-40" /> : (
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          {field("whatsapp", "WhatsApp number", "+91XXXXXXXXXX")}
          {field("phone", "Support phone", "+91XXXXXXXXXX")}
          {field("email", "Support email", "support@zrope.in")}
          {field("display_name", "Display name", "ZRo Pe Support")}
          <div className="sm:col-span-2"><button onClick={save} disabled={saving} data-testid="support-contact-save" className="rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0D9488] disabled:opacity-60">{saving ? "Saving…" : "Save Changes"}</button></div>
        </div>
      )}
    </div>
  );
}

function IntegrationSheet() {
  const [busy, setBusy] = useState("");
  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const { data } = await api.get(`/admin/integration-sheet.${fmt}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url; a.download = `ZRoPe_API_Integration_Requirement_Sheet.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="admin-integration-sheet">
      <h3 className="flex items-center gap-2 font-display font-bold text-[#0B132B]"><FileSpreadsheet size={16} /> API Integration Requirement Sheet</h3>
      <p className="mt-1 text-xs text-slate-400">A ready-to-share checklist of everything ZRo Pe requires from a Bank / NBFC / Lender / API provider for integration (institution details, API access, loan-data & payment APIs, webhooks, docs, security, and a sample data format). This is a requirement template, not a claim of current support.</p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button onClick={() => download("xlsx")} disabled={busy === "xlsx"} data-testid="integration-sheet-xlsx"
          className="flex items-center gap-2 rounded-lg bg-[#14B8A6] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#0D9488] disabled:opacity-60">
          <Download size={15} /> {busy === "xlsx" ? "Preparing…" : "Download Excel (.xlsx)"}
        </button>
        <button onClick={() => download("txt")} disabled={busy === "txt"} data-testid="integration-sheet-txt"
          className="flex items-center gap-2 rounded-lg border border-slate-300 px-4 py-2 text-sm font-bold text-[#0B132B] transition hover:border-[#14B8A6] disabled:opacity-60">
          <Download size={15} /> {busy === "txt" ? "Preparing…" : "Download Text (.txt)"}
        </button>
      </div>
    </div>
  );
}

export function AdminSettings() {
  const [d] = useFetch("/admin/settings");
  return (
    <Page title="Settings" subtitle="Integration status & platform configuration">
      <FeatureControls />
      <SupportContactSettings />
      <IntegrationSheet />
      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="flex items-center gap-2 font-display font-bold text-[#0B132B]"><SettingsIcon size={16} /> Integrations</h3>
        {!d ? <Skeleton className="mt-3 h-40" /> : (
          <>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              {Object.entries(d.integrations || {}).map(([k, v]) => {
                const live = String(v).toUpperCase().includes("LIVE");
                return (
                  <div key={k} className="flex items-center justify-between rounded-md bg-slate-50 px-4 py-3 text-sm">
                    <span className="font-medium capitalize text-[#0B132B]">{k.replace(/_/g, " ")}</span>
                    <span className={`rounded-md px-2 py-0.5 text-xs font-bold ${live ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{v}</span>
                  </div>
                );
              })}
            </div>
            <p className="mt-4 text-xs text-slate-400">All integration credentials are stored as server-side environment variables and never exposed to the client.</p>
          </>
        )}
      </div>
    </Page>
  );
}
